import { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { L10nContext } from 'context/L10nContext';

import { getSequenceDefinition, getSequenceTypeDefinition } from 'services/device/domain/business/inventory/SequenceTypeDefinition';
import { SequenceViewModel } from 'services/device/domain/model/SequenceModel';
import { OutputListSelection } from 'services/device/presentation/ui/widget/widget-renderer/OutputListSelection';

import { useTypedSelector } from 'store/common/TypedSelector';
import { AsyncFetchStatus } from 'store/common/AsyncFetchStatus';
import {
	fetchExternalReportRecordsBySequence,
	selectExternalReportInitialRecordBySequence
} from '../../../../store/externalReportRecordSlice';

export interface ExternalReportOutputListProps {
	sequenceViewModel: SequenceViewModel;
	reportUuid: string;
}

export const ExternalReportOutputListRenderer = (props: ExternalReportOutputListProps): JSX.Element => {
	const { sequenceViewModel, reportUuid } = props;

	const l10nContext = useContext(L10nContext);

	// Consume the dispatch object
	const dispatch = useDispatch();

	// Use a custom selector function to provide type information for the state defined in the slice
	const externalReportRecordsStoreFetchStatus = useTypedSelector(state => state.externalReportRecords.fetchStatus);

	const sequenceUuid = sequenceViewModel.Uuid;

	// Read records from the server
	useEffect(() => {
		if (externalReportRecordsStoreFetchStatus === AsyncFetchStatus.INITIAL || externalReportRecordsStoreFetchStatus === AsyncFetchStatus.IDLE) {
			dispatch(fetchExternalReportRecordsBySequence({ sequenceUuid, reportUuid }));
		}
	});

	const sequenceTypeDefinition = getSequenceTypeDefinition(sequenceViewModel.Type);

	// We don't get a sequence here, an error occurs
	const sequenceDefinition = getSequenceDefinition(sequenceViewModel.DeviceType, sequenceViewModel.Type);
	const sequence = sequenceDefinition.restoreSequenceFromViewData(sequenceViewModel.SequenceConfiguration);

	const initialRecordViewModel = useSelector(selectExternalReportInitialRecordBySequence(sequenceViewModel));

	let initialRecord = null;
	if (initialRecordViewModel !== null) {
		initialRecord = sequenceTypeDefinition.restoreRecordFromViewData(initialRecordViewModel.RecordConfiguration);
	}

	const outputNodes: Array<JSX.Element> = [];

	for (const outputDescription of sequenceTypeDefinition.getDetailsDescription().entries) {
		sequence.getOutput(outputDescription).selectOutput(
			new OutputListSelection((element): void => {
				outputNodes.push(
					<li key={outputDescription.name}>
						<strong>{l10nContext.translate(outputDescription.labelKey)}:&nbsp;</strong>
						<p>{element}</p>
					</li>
				);
			})
		);
	}

	if (
		sequenceTypeDefinition.getDetailsDescription().showInitialValue
		&& initialRecord !== null
	) {
		outputNodes.push(
			<li key="initialValueRecordedAt">
				<strong>{l10nContext.translate('sequence.attributes.initialValueRecordedAt')}:&nbsp;</strong>
				<p>{l10nContext.formatDateTime(initialRecordViewModel?.RecordConfiguration.values.recordedAt)}</p>
			</li>
		);

		const valueOutputDescription = sequenceTypeDefinition.getValueDescription();
		initialRecord.getOutput(valueOutputDescription).selectOutput(
			new OutputListSelection((element): void => {
				outputNodes.push(
					<li key={valueOutputDescription.name}>
						<strong>{l10nContext.translate(valueOutputDescription.labelKey)}:&nbsp;</strong>
						<p>{element}</p>
					</li>
				);
			})
		);
	}

	return (
		<ul>
			{outputNodes}
		</ul>
	);

};
